import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Feature Flags Context
 * Provides feature flag values throughout the application.
 * Flags are fetched from the backend and cached.
 */

const FeatureFlagsContext = createContext(null);

// Default flags (used before backend response)
const DEFAULT_FLAGS = {
  assessments_nav_visible: true,
  assessments_module_enabled: false,
  assessments_pilot_mode: false,
  assessments_template_builder_enabled: false,
  assessments_scoring_enabled: false,
  assessments_admin_preview: true,
  billing_module_visible: false,
  billing_module_enabled: false,
  csv_import_progress_enabled: true,
  ai_summary_enabled: true,
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    // Return defaults if context not available
    return { flags: DEFAULT_FLAGS, loading: true, refetch: () => {} };
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  const fetchFlags = async () => {
    if (!isAuthenticated) {
      // Reset to defaults when not authenticated
      setFlags(DEFAULT_FLAGS);
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.getFeatureFlags();
      setFlags(response.data);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      // Use defaults on error
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset to defaults immediately when user changes to prevent stale flags
    setFlags(DEFAULT_FLAGS);
    setLoading(true);
    fetchFlags();
  }, [isAuthenticated, user?.id]);

  // Helper functions for common flag checks
  const canAccessAssessments = () => {
    // Super admin with preview access can always access
    if (user?.role === 'super_admin' && flags.assessments_admin_preview) {
      return true;
    }
    // Otherwise, module must be enabled
    return flags.assessments_module_enabled;
  };

  const showAssessmentsNav = () => {
    return flags.assessments_nav_visible;
  };

  const isAssessmentsComingSoon = () => {
    return flags.assessments_nav_visible && !flags.assessments_module_enabled;
  };

  const value = {
    flags,
    loading,
    refetch: fetchFlags,
    // Convenience helpers
    canAccessAssessments,
    showAssessmentsNav,
    isAssessmentsComingSoon,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
